import type { Creche } from "../data/creches";
import { formatAdresseComplete, libelleExterieur } from "../data/creches";
import { site } from "../data/site";

export type JsonLd = Record<string, unknown>;

export const websiteJsonLd = (): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: site.name,
  url: site.url,
  inLanguage: "fr-FR",
});

export const organizationJsonLd = (): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: site.legalName,
  alternateName: site.name,
  url: site.url,
  logo: `${site.url}/images/logo.webp`,
  description: site.description,
  foundingDate: site.organization.foundingDate,
  areaServed: site.organization.areaServed,
  sameAs: [site.socialLinks.facebook],
});

export const crecheJsonLd = (c: Creche): JsonLd => {
  const url = `${site.url}/micro-creches/${c.slug}`;
  const images = [c.photoPrincipale, ...(c.galerie ?? [])]
    .filter((p): p is string => Boolean(p))
    .map((p) => `${site.url}${p}`);
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ChildCare"],
    "@id": `${url}#localbusiness`,
    name: `La La Land ${c.nom}`,
    url,
    image: images.length > 0 ? images : `${site.url}${c.photoPrincipale}`,
    logo: `${site.url}/images/logo.webp`,
    description: `Micro-crèche La La Land à ${c.ville} : ${c.capacite} enfants, ${c.superficie} m²${c.superficieJardin > 0 ? ` + ${libelleExterieur(c).toLowerCase()} de ${c.superficieJardin} m²` : ""}. Pédagogie musicale, accueil de ${c.agesAccueillis}.`,
    priceRange: "€€",
    address: {
      "@type": "PostalAddress",
      streetAddress: c.adresse,
      postalCode: c.codePostal,
      addressLocality: c.ville,
      addressRegion: "Normandie",
      addressCountry: "FR",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: c.coordonnees.lat,
      longitude: c.coordonnees.lng,
    },
    email: c.email !== "À compléter" ? c.email : undefined,
    telephone: c.telephone,
    openingHoursSpecification: c.horaires.jours.map((j) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: `https://schema.org/${jourToSchema[j] ?? j}`,
      opens: c.horaires.ouverture,
      closes: c.horaires.fermeture,
    })),
    parentOrganization: {
      "@type": "Organization",
      name: site.legalName,
      url: site.url,
    },
    areaServed: {
      "@type": "City",
      name: c.ville,
    },
    audience: {
      "@type": "PeopleAudience",
      suggestedMinAge: 0,
      suggestedMaxAge: 5,
    },
    knowsLanguage: "fr-FR",
    isAccessibleForFree: false,
    additionalProperty: [
      {
        "@type": "PropertyValue",
        name: "Capacité",
        value: c.capacite,
      },
      {
        "@type": "PropertyValue",
        name: "Superficie totale",
        value: `${c.superficie} m²`,
      },
      ...(c.superficieJardin > 0
        ? [
            {
              "@type": "PropertyValue",
              name: `Superficie ${libelleExterieur(c).toLowerCase()}`,
              value: `${c.superficieJardin} m²`,
            },
          ]
        : []),
      {
        "@type": "PropertyValue",
        name: "Âges accueillis",
        value: c.agesAccueillis,
      },
    ],
  };
};

export const crechesItemListJsonLd = (list: Creche[]): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListOrder: "https://schema.org/ItemListUnordered",
  numberOfItems: list.length,
  itemListElement: list.map((c, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    url: `${site.url}/micro-creches/${c.slug}`,
    name: `La La Land ${c.nom}`,
  })),
});

export const breadcrumbJsonLd = (
  items: Array<{ name: string; url: string }>,
): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: it.name,
    item: it.url,
  })),
});

const jourToSchema: Record<string, string> = {};
function setJours() {
  const map: [string, string][] = [
    ["Lundi", "Monday"],
    ["Mardi", "Tuesday"],
    ["Mercredi", "Wednesday"],
    ["Jeudi", "Thursday"],
    ["Vendredi", "Friday"],
    ["Samedi", "Saturday"],
    ["Dimanche", "Sunday"],
  ];
  for (const [fr, en] of map) jourToSchema[fr] = en;
}
setJours();

export { formatAdresseComplete };
